import React, { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useContactInfo } from '../hooks/useContentful';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailjs';
import Alert from './Alert';
import '../styles/recaptcha.css';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const ContactSection = () => {
  // TODO: When Contentful is connected, use this for contact information
  const { contactInfo, loading, error } = useContactInfo();
  const recaptchaRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('contacto'); // 'contacto' or 'sugerencia'
  
  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    mensaje: ''
  });

  // reCAPTCHA and Alert state
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [alert, setAlert] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle reCAPTCHA change (not used in invisible mode, kept for reference)
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  // Reset reCAPTCHA cuando cambia el tab
  useEffect(() => {
    setRecaptchaToken(null);
  }, [activeTab]);





  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const validateForm = () => {
    if (!formData.nombre.trim()) {
      setAlert({ type: 'warning', message: 'Por favor, ingresa tu nombre completo.' });
      return false;
    }
    if (!formData.email.trim()) {
      setAlert({ type: 'warning', message: 'Por favor, ingresa tu correo electrónico.' });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setAlert({ type: 'warning', message: 'Por favor, ingresa un correo válido.' });
      return false;
    }
    if (!formData.telefono.trim()) {
      setAlert({ type: 'warning', message: 'Por favor, ingresa tu número de teléfono.' });
      return false;
    }
    if (!formData.mensaje.trim()) {
      setAlert({ type: 'warning', message: 'Por favor, ingresa tu mensaje.' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = await recaptchaRef.current.executeAsync();
      
      if (!token) {
        setAlert({ type: 'error', message: 'Error en la verificación de reCAPTCHA. Por favor, intenta nuevamente.' });
        setIsSubmitting(false);
        return;
      }

      // Prepare template parameters
    const templateParams = {
  from_name: formData.nombre,
  from_email: formData.email,
  phone: formData.telefono,
  message: formData.mensaje,
  to_name: 'Equipo de Atención',
  subject_type: activeTab === 'contacto' ? 'Consulta General' : 'Sugerencia',
  time: new Date().toLocaleString('es-AR'),
  'g-recaptcha-response': token
};

      console.log('Enviando parámetros:', templateParams);

      const templateId = activeTab === 'contacto' 
        ? EMAILJS_CONFIG.TEMPLATE_CONTACTO 
        : EMAILJS_CONFIG.TEMPLATE_SUGERENCIA;

      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        templateId,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      setAlert({
        type: 'success',
        message: activeTab === 'contacto' 
          ? 'Mensaje enviado correctamente. Te contactaremos pronto.'
          : 'Sugerencia enviada correctamente. Gracias por tu aporte.'
      });

      // Reset form
      resetForm();
      setRecaptchaToken(null);
    } catch (error) {
      console.error('Error sending email:', error);
      setAlert({
        type: 'error',
        message: 'Hubo un error al enviar tu mensaje. Por favor, intenta nuevamente o contáctanos directamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      telefono: '',
      mensaje: ''
    });
  };

  if (loading) {
    return (
      <section id="contacto" className="py-12 md:py-20 bg-gray-50 relative">
        {/* Section Separator */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-300 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12">
            {/* Left side skeleton */}
            <div>
              <div className="h-8 bg-gradient-to-r from-purple-300 to-purple-400 rounded-lg w-3/4 mb-6 animate-shimmer-purple"></div>
              <div className="h-6 bg-gradient-to-r from-purple-200 to-purple-300 rounded-lg w-full mb-8 animate-shimmer-purple"></div>
              <div className="space-y-6">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="flex items-start" style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-500 rounded-full flex-shrink-0 animate-glow"></div>
                    <div className="ml-4 flex-1 space-y-2">
                      <div className="h-4 bg-gradient-to-r from-purple-200 to-purple-300 rounded w-1/2 animate-shimmer-purple"></div>
                      <div className="h-3 bg-gradient-to-r from-purple-100 to-purple-200 rounded w-3/4 animate-shimmer-purple"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Right side (form) skeleton */}
            <div className="mt-10 lg:mt-0">
              <div className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl border border-purple-200 shadow-md">
                <div className="h-6 bg-gradient-to-r from-purple-300 to-purple-400 rounded-lg w-1/2 mx-auto mb-6 animate-shimmer-purple"></div>
                <div className="space-y-5">
                  {[...Array(4)].map((_, index) => (
                    <div key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                      <div className="h-4 bg-gradient-to-r from-purple-200 to-purple-300 rounded w-1/3 mb-2 animate-shimmer-purple"></div>
                      <div className="h-10 bg-gradient-to-r from-purple-100 to-purple-200 rounded-lg animate-shimmer-purple"></div>
                    </div>
                  ))}
                  <div className="h-12 bg-gradient-to-r from-purple-400 to-purple-500 rounded-lg animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    // Fallback to static content
  }

  const contactData = contactInfo;

  return (
    <section id="contacto" className="py-16 md:py-24 bg-gradient-to-br from-purple-50 via-white to-pink-50 relative overflow-visible">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
          autoClose={true}
          duration={5000}
        />
      )}
      
      {/* Section Separator */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-400 to-pink-400 to-transparent"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-purple-200/20 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-pink-200/15 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-12">
          {/* Contact Information */}
          <div className="animate-fadeInUp mb-8 lg:mb-0" style={{animationDelay: '0.2s'}}>
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="fas fa-envelope"></i>
              Contáctanos
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-800 mb-4 md:mb-6 leading-tight">
              Contáctanos <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">ahora</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed max-w-2xl">
              Estamos disponibles las 24 horas del día, los 7 días de la semana para atender todas tus emergencias veterinarias y consultas. Tu tranquilidad es nuestra prioridad.
            </p>
            
            <div className="space-y-6 md:space-y-8">
              {/* Address */}
              <div className="flex items-start group">
                <div className="flex-shrink-0 mt-1 w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center transition-all duration-300 group-hover:from-purple-600 group-hover:to-pink-600 transform group-hover:scale-110">
                  <i className="fas fa-map-marker-alt text-purple-600 text-lg md:text-xl group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-1">Nuestra Dirección</h3>
                  <p className="text-base md:text-lg text-gray-700">
                    {/* TODO: Use contactInfo?.address from Contentful */}
                    { contactInfo?.address}
                  </p>
                </div>
              </div>
              
              {/* Phone */}
              <div className="flex items-start group">
                <div className="flex-shrink-0 mt-1 w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center transition-all duration-300 group-hover:from-purple-600 group-hover:to-pink-600 transform group-hover:scale-110">
                  <i className="fas fa-phone-alt text-purple-600 text-lg md:text-xl group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-1">Teléfono de Emergencias</h3>
                  <p className="text-base md:text-lg text-gray-700 font-bold text-purple-600">
                    {/* TODO: Use contactInfo?.phone from Contentful */}
                    {contactData.phone} <span className="font-normal text-gray-600 block md:inline">(Línea 24 horas)</span>
                  </p>
                </div>
              </div>
              
              {/* Email */}
              <div className="flex items-start group">
                <div className="flex-shrink-0 mt-1 w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center transition-all duration-300 group-hover:from-purple-600 group-hover:to-pink-600 transform group-hover:scale-110">
                  <i className="fas fa-envelope text-purple-600 text-lg md:text-xl group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-1">Correo Electrónico</h3>
                  <p className="text-base md:text-lg text-gray-700 break-all">
                    {/* TODO: Use contactInfo?.email from Contentful */}
                    {contactData.email}
                  </p>
                </div>
              </div>
              
              {/* Schedule */}
              <div className="flex items-start group">
                <div className="flex-shrink-0 mt-1 w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center transition-all duration-300 group-hover:from-purple-600 group-hover:to-pink-600 transform group-hover:scale-110">
                  <i className="fas fa-clock text-purple-600 text-lg md:text-xl group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-1">Horario de Atención</h3>
                  <p className="text-base md:text-lg text-gray-700">
                    {/* TODO: Use contactInfo?.schedule from Contentful */}
                    {contactData.schedule}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="animate-fadeInUp" style={{animationDelay: '0.4s'}}>
            <div className={`${activeTab === 'contacto' ? 'bg-gradient-to-br from-purple-50 to-pink-50' : 'bg-gradient-to-br from-orange-50 to-red-50'} p-8 md:p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-purple-200/50`} style={{ overflow: 'visible' }}>
              {/* Tabs */}
              <div className="flex mb-6 bg-gray-100 rounded-xl p-1">
                <button
                  type="button"
                  onClick={() => setActiveTab('contacto')}
                  className={`flex-1 py-3 px-4 rounded-lg text-sm md:text-base font-medium transition-all duration-300 ${
                    activeTab === 'contacto'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                      : 'text-gray-600 hover:text-purple-600'
                  }`}
                >
                  <i className="fas fa-comments mr-2"></i>
                  Contacto
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('sugerencia')}
                  className={`flex-1 py-3 px-4 rounded-lg text-sm md:text-base font-medium transition-all duration-300 ${
                    activeTab === 'sugerencia'
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-orange-500'
                  }`}
                >
                  <i className="fas fa-comment-dots mr-2"></i>
                  Sugerencia
                </button>
              </div>

              <form onSubmit={handleSubmit} style={{ overflow: 'visible' }}>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                  {activeTab === 'contacto' ? 'Envíanos un Mensaje' : 'Envíanos tu Sugerencia'}
                </h3>
              
              <div className="mb-4 md:mb-5">
                <label htmlFor="nombre" className="block text-gray-700 font-medium mb-1 md:mb-2 text-sm md:text-base">
                  Nombre Completo
                </label>
                <input 
                  type="text" 
                  id="nombre" 
                  value={formData.nombre}
                  onChange={handleInputChange}
                  className={`w-full px-3 md:px-5 py-2 md:py-3 border rounded-lg focus:outline-none focus:ring-3 focus:border-transparent transition-all duration-300 text-sm md:text-base ${
                    activeTab === 'contacto' 
                      ? 'border-purple-300 focus:ring-purple-500' 
                      : 'border-orange-300 focus:ring-orange-500'
                  }`}
                  placeholder="Tu nombre"
                  required
                />
              </div>
              
              <div className="mb-4 md:mb-5">
                <label htmlFor="email" className="block text-gray-700 font-medium mb-1 md:mb-2 text-sm md:text-base">
                  Correo Electrónico
                </label>
                <input 
                  type="email" 
                  id="email" 
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 md:px-5 py-2 md:py-3 border rounded-lg focus:outline-none focus:ring-3 focus:border-transparent transition-all duration-300 text-sm md:text-base ${
                    activeTab === 'contacto' 
                      ? 'border-purple-300 focus:ring-purple-500' 
                      : 'border-orange-300 focus:ring-orange-500'
                  }`}
                  placeholder="tu.email@ejemplo.com"
                  required
                />
              </div>
              
              <div className="mb-4 md:mb-5">
                <label htmlFor="telefono" className="block text-gray-700 font-medium mb-1 md:mb-2 text-sm md:text-base">
                  Número de Teléfono
                </label>
                <input 
                  type="tel" 
                  id="telefono" 
                  value={formData.telefono}
                  onChange={handleInputChange}
                  className={`w-full px-3 md:px-5 py-2 md:py-3 border rounded-lg focus:outline-none focus:ring-3 focus:border-transparent transition-all duration-300 text-sm md:text-base ${
                    activeTab === 'contacto' 
                      ? 'border-purple-300 focus:ring-purple-500' 
                      : 'border-orange-300 focus:ring-orange-500'
                  }`}
                  placeholder="+XX XXX XXX XXXX"
                />
              </div>
              
              <div className="mb-4 md:mb-6">
                <label htmlFor="mensaje" className="block text-gray-700 font-medium mb-1 md:mb-2 text-sm md:text-base">
                  Tu Mensaje
                </label>
                <textarea 
                  id="mensaje" 
                  rows="4" 
                  value={formData.mensaje}
                  onChange={handleInputChange}
                  className={`w-full px-3 md:px-5 py-2 md:py-3 border rounded-lg focus:outline-none focus:ring-3 focus:border-transparent transition-all duration-300 text-sm md:text-base ${
                    activeTab === 'contacto' 
                      ? 'border-purple-300 focus:ring-purple-500' 
                      : 'border-orange-300 focus:ring-orange-500'
                  }`}
                  placeholder={activeTab === 'contacto' 
                    ? "Describe tu consulta o emergencia aquí..." 
                    : "Describe tu sugerencia o comentario aquí..."
                  }
                  required
                ></textarea>
              </div>

              {/* reCAPTCHA Invisible */}
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
                size="invisible"
              />
              
              <button 
                type="submit"
                disabled={isSubmitting}
                className={`w-full font-bold py-4 md:py-5 px-6 md:px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-500 text-white text-lg md:text-xl ${
                  activeTab === 'contacto' 
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-purple-400 disabled:to-pink-400' 
                    : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:from-orange-400 disabled:to-red-400'
                } disabled:cursor-not-allowed disabled:hover:scale-100`}
              >
                <i className={`fas ${activeTab === 'contacto' ? 'fa-paper-plane' : 'fa-exclamation-circle'} mr-2`}></i> 
                {isSubmitting ? 'Verificando...' : activeTab === 'contacto' ? 'Enviar Mensaje' : 'Enviar Sugerencia'}
              </button>

              {/* Legal Text - reCAPTCHA */}
              <div className="mt-4 md:mt-5 text-center px-2">
                <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                  Este sitio está protegido por reCAPTCHA y se aplican la 
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 hover:underline transition-colors duration-200 mx-1"
                  >
                    Política de Privacidad
                  </a>
                  y los 
                  <a 
                    href="https://policies.google.com/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 hover:underline transition-colors duration-200 mx-1"
                  >
                    Términos de Servicio
                  </a>
                  de Google.
                </p>
              </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;